import { useMemo, type ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Card, CardBody } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { IconShield } from '@/components/Icon'
import { TELAS, podeAcessar, rotaInicial } from '@/lib/permissoes'
import { useSessao } from '@/lib/SessaoProvider'

/**
 * Guarda de rota.
 *
 * Esconder o item no menu não basta: o usuário pode digitar `/duimp` na barra de
 * endereço, usar um link antigo ou um favorito. Sem guarda, ele veria a tela
 * carregar e falhar com erro técnico do backend.
 *
 * Aqui a resposta é explícita: diz qual tela foi pedida, que falta permissão,
 * e oferece caminho de volta — voltar à página anterior ou ir para uma tela que
 * ele realmente acessa.
 *
 * ⚠️ Isto é experiência de uso, **não** segurança: quem chamar o endpoint direto
 * não passa por aqui. O bloqueio real é do servidor.
 */
export function Protegido({ rota, children }: { rota: string; children: ReactNode }) {
  const sessao = useSessao()
  const permitido = podeAcessar(sessao, rota)

  if (permitido) return <>{children}</>
  return <SemPermissao rota={rota} />
}

export function SemPermissao({ rota }: { rota?: string }) {
  const sessao = useSessao()
  const navegar = useNavigate()
  const { pathname } = useLocation()
  const alvo = rota ?? pathname

  const nomeDaTela = useMemo(
    () => TELAS.find((t) => alvo === t.chave || alvo.startsWith(`${t.chave}/`))?.label,
    [alvo],
  )

  const destino = rotaInicial(sessao)
  const nomeDestino = TELAS.find((t) => t.chave === destino)?.label ?? 'início'

  /** Se o usuário não tem nenhuma tela, orientar é mais útil que oferecer link. */
  const semNenhumAcesso = destino === '/login'

  return (
    <div className="flex min-h-[60dvh] items-center justify-center">
      <Card className="max-w-md">
        <CardBody className="text-center">
          <span className="mx-auto flex size-10 items-center justify-center rounded-lg bg-warning-50 text-warning-600">
            <IconShield className="size-5" />
          </span>

          <h1 className="mt-3 text-base font-semibold text-ink-800">Sem permissão de acesso</h1>

          <p className="mt-1.5 text-sm text-ink-600">
            {nomeDaTela ? (
              <>
                Seu usuário não tem acesso à tela{' '}
                <strong className="text-ink-800">{nomeDaTela}</strong>.
              </>
            ) : (
              <>
                Seu usuário não tem acesso a{' '}
                <code className="rounded bg-ink-100 px-1 font-mono text-xs">{alvo}</code>.
              </>
            )}
          </p>

          <p className="mt-2 text-xs text-ink-500">
            {semNenhumAcesso
              ? 'Nenhuma tela foi liberada para o seu usuário. Peça ao administrador para configurar seu acesso.'
              : 'Se você precisa desta tela no seu trabalho, peça ao administrador para liberar.'}
          </p>

          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            <Button variant="secondary" onClick={() => navegar(-1)}>
              Voltar
            </Button>
            {!semNenhumAcesso && (
              <Link to={destino}>
                <Button variant="primary">Ir para {nomeDestino}</Button>
              </Link>
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
